/**
 * Conversion between EditorModbusTemplate (UI state) and
 * ModbusTemplateT (FlatBuffer Object API) for binary save/load.
 *
 * The config schema stores ln2_message as Ln2.ValuedMessage — the full
 * message type IS built at adapter config time (category, variant, enum
 * value, active) using compositeKeyToValuedMessageT. At bank/lift
 * expansion time, the server wraps the ValuedMessageT in FrontEndMessageT
 * with bank_id and lift_id from bank context.
 *
 * The label field is NOT used for message identity — ValuedMessageT is
 * the sole source of truth. On load, the composite key for UI display
 * is derived from the ValuedMessageT via valuedMessageTToCompositeKey().
 * The label field falls back for legacy CDOs saved before ValuedMessageT.
 */
import * as flatbuffers from "flatbuffers";

import { ModbusTemplate, ModbusTemplateT } from "../flatbuffers/generated/config/modbus-template";
import { ModbusReadEntryT } from "../flatbuffers/generated/config/modbus-read-entry";
import { ModbusWriteEntryT } from "../flatbuffers/generated/config/modbus-write-entry";
import { ModbusRegisterOffsetMapT } from "../flatbuffers/generated/config/modbus-register-offset-map";
import type {
  EditorReadEntry,
  EditorWriteEntry,
  EditorModbusTemplate,
} from "../pages/setupWizard/steps/CdoModbusEditor";
import {
  compositeKeyToValuedMessageT,
  valuedMessageTToCompositeKey,
} from "../flatbuffers/types/valuedMessageBuilder";

/**
 * Per-register-type Initial/Delta offsets shape used by the AdapterEditor
 * matrix UI. Each register space (COIL/DI/HR/IR) has an `initial` byte/
 * register address and a `delta` stride applied per lift at bank-expansion
 * time. Stored on the editor side as nested {initial,delta} maps so the
 * matrix's `value[col.key]` lookup stays trivial, but on the wire (and in
 * the FlatBuffer schema) the same data is flattened to 8 int32 fields on
 * ModbusRegisterOffsetMapT.
 */
export type ModbusRegisterOffsetsUi = {
  initial: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
  delta: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
};

/**
 * Convert the UI nested {initial,delta} shape to the FlatBuffer Object API
 * flat `ModbusRegisterOffsetMapT`. Returns null when every COIL/DI/HR/IR
 * offset is 0 (or absent) so the FB pack omits an empty table — keeps the
 * binary tight and lets the backend treat missing data as "no expansion".
 */
export function modbusRegisterOffsetsUiToFb(
  ui: ModbusRegisterOffsetsUi | undefined | null,
): ModbusRegisterOffsetMapT | null {
  if (!ui) return null;
  const initial = ui.initial ?? {};
  const delta = ui.delta ?? {};
  const fb = new ModbusRegisterOffsetMapT(
    initial.COIL ?? 0,
    delta.COIL ?? 0,
    initial.DI ?? 0,
    delta.DI ?? 0,
    initial.HR ?? 0,
    delta.HR ?? 0,
    initial.IR ?? 0,
    delta.IR ?? 0,
  );
  if (
    fb.coilInitial === 0 &&
    fb.coilDelta === 0 &&
    fb.diInitial === 0 &&
    fb.diDelta === 0 &&
    fb.hrInitial === 0 &&
    fb.hrDelta === 0 &&
    fb.irInitial === 0 &&
    fb.irDelta === 0
  ) {
    return null;
  }
  return fb;
}

/**
 * Inverse of `modbusRegisterOffsetsUiToFb` — convert a `ModbusRegisterOffsetMapT`
 * (8 flat fields) back to the matrix-friendly nested {initial,delta} shape.
 * Returns null when the input is null so callers can pattern-match on the
 * "no register-level expansion set" state.
 */
export function modbusRegisterOffsetsFbToUi(
  fb: ModbusRegisterOffsetMapT | null | undefined,
): ModbusRegisterOffsetsUi | null {
  if (!fb) return null;
  return {
    initial: {
      COIL: fb.coilInitial,
      DI: fb.diInitial,
      HR: fb.hrInitial,
      IR: fb.irInitial,
    },
    delta: {
      COIL: fb.coilDelta,
      DI: fb.diDelta,
      HR: fb.hrDelta,
      IR: fb.irDelta,
    },
  };
}

/**
 * Permissive parser for the per-register-type offsets that the backend
 * might send back in the `cdo_template` JSON column. Handles every shape
 * an adapter row can carry today:
 *
 *  - Modern FlatBuffer-shape JSON: `register_offsets: { coil_initial, ... }`
 *    (emitted by Go's `json.Marshal(ModbusTemplateT)`).
 *  - Frontend-roundtrip JSON: `registerOffsets: { coilInitial, ... }`
 *    (emitted by `JSON.stringify(ModbusTemplateT)` on the TS class).
 *  - Legacy nested shape: `modbusRegisterOffsets: { initial: {...}, delta: {...} }`
 *    (saved by older AdapterEditor versions before the FBS field existed).
 *
 * Returns null if none of those shapes is present so callers can fall
 * back to the FlatBuffer-binary unpack as the canonical source.
 */
export function parseModbusRegisterOffsetsFromCdoTemplateJson(
  parsed: Record<string, unknown> | null | undefined,
): ModbusRegisterOffsetsUi | null {
  if (!parsed) return null;
  // FlatBuffer-shape (snake_case) — preferred path: Go's json.Marshal of
  // ModbusTemplateT emits exactly this.
  const snake = parsed.register_offsets as Record<string, number> | undefined;
  if (snake && typeof snake === "object") {
    return {
      initial: {
        COIL: Number(snake.coil_initial ?? 0),
        DI: Number(snake.di_initial ?? 0),
        HR: Number(snake.hr_initial ?? 0),
        IR: Number(snake.ir_initial ?? 0),
      },
      delta: {
        COIL: Number(snake.coil_delta ?? 0),
        DI: Number(snake.di_delta ?? 0),
        HR: Number(snake.hr_delta ?? 0),
        IR: Number(snake.ir_delta ?? 0),
      },
    };
  }
  // FlatBuffer-shape (camelCase) — TS class field naming, what
  // `JSON.stringify(modbusTemplateT)` produces.
  const camel = parsed.registerOffsets as Record<string, number> | undefined;
  if (camel && typeof camel === "object") {
    return {
      initial: {
        COIL: Number(camel.coilInitial ?? 0),
        DI: Number(camel.diInitial ?? 0),
        HR: Number(camel.hrInitial ?? 0),
        IR: Number(camel.irInitial ?? 0),
      },
      delta: {
        COIL: Number(camel.coilDelta ?? 0),
        DI: Number(camel.diDelta ?? 0),
        HR: Number(camel.hrDelta ?? 0),
        IR: Number(camel.irDelta ?? 0),
      },
    };
  }
  // Legacy nested shape — older AdapterEditor versions wrote this.
  const legacy = parsed.modbusRegisterOffsets as
    { initial?: Record<string, number>; delta?: Record<string, number> } | undefined;
  if (legacy && typeof legacy === "object") {
    const lInit = legacy.initial ?? {};
    const lDelta = legacy.delta ?? {};
    return {
      initial: {
        COIL: Number(lInit.COIL ?? 0),
        DI: Number(lInit.DI ?? 0),
        HR: Number(lInit.HR ?? 0),
        IR: Number(lInit.IR ?? 0),
      },
      delta: {
        COIL: Number(lDelta.COIL ?? 0),
        DI: Number(lDelta.DI ?? 0),
        HR: Number(lDelta.HR ?? 0),
        IR: Number(lDelta.IR ?? 0),
      },
    };
  }
  return null;
}

// =====================================================================
// Editor -> FlatBuffer
// =====================================================================

/** Convert an EditorReadEntry to a ModbusReadEntryT. */
function editorReadToFb(entry: EditorReadEntry): ModbusReadEntryT {
  const ln2Message = entry.msgId ? compositeKeyToValuedMessageT(entry.msgId, entry.active) : null;
  const pairedLn2Message = entry.pairedMsgId
    ? compositeKeyToValuedMessageT(entry.pairedMsgId, entry.pairedActive)
    : null;
  return new ModbusReadEntryT(
    entry.registerAddress,
    entry.readType,
    entry.quantity,
    entry.interpretation,
    entry.bit,
    entry.bitsPerRegister,
    entry.expectedValue,
    ln2Message,
    entry.floorsBased,
    entry.unitsBased,
    null, // label no longer stored; derived from ValuedMessageT on load
    undefined, // dataTypeType — default Value.NONE
    null, // dataType
    undefined, // wordOrder — default BigEndianWordFirst
    pairedLn2Message,
  );
}

/** Convert an EditorWriteEntry to a ModbusWriteEntryT. */
function editorWriteToFb(entry: EditorWriteEntry): ModbusWriteEntryT {
  const ln2Message = entry.msgId ? compositeKeyToValuedMessageT(entry.msgId, entry.active) : null;
  return new ModbusWriteEntryT(
    entry.registerAddress,
    entry.writeType,
    entry.interpretation,
    entry.bit,
    entry.bitsPerRegister,
    entry.andMask,
    entry.orMask,
    ln2Message,
    entry.floorsBased,
    entry.unitsBased,
    null, // label no longer stored; derived from ValuedMessageT on load
    entry.writeValue,
  );
}

/**
 * Convert the UI editor state into a FlatBuffer Object API type
 * ready for packing into binary. `registerOffsets` (matrix-style
 * nested {initial,delta} map) is flattened into the schema's
 * `ModbusRegisterOffsetMapT` slot so adapter-level Initial/Delta
 * survives the FlatBuffer round-trip — without the field the
 * pack-then-unpack cycle silently zeroes the user's offsets.
 */
export function editorToModbusTemplateT(editor: EditorModbusTemplate): ModbusTemplateT {
  return new ModbusTemplateT(
    (editor.reads ?? []).map(editorReadToFb),
    (editor.writes ?? []).map(editorWriteToFb),
    editor.slaveId,
    editor.pollingInterval,
    modbusRegisterOffsetsUiToFb(editor.registerOffsets ?? null),
  );
}

/**
 * Pack the editor state directly into FlatBuffer binary bytes.
 */
export function packModbusTemplate(editor: EditorModbusTemplate): Uint8Array {
  const t = editorToModbusTemplateT(editor);
  const builder = new flatbuffers.Builder(1024);
  const offset = t.pack(builder);
  builder.finish(offset);
  return builder.asUint8Array();
}

// =====================================================================
// FlatBuffer -> Editor
// =====================================================================

/** Decode a label that may be a string or Uint8Array from FlatBuffer unpack. */
function decodeLabel(raw: string | Uint8Array | null): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") return raw;
  return new TextDecoder().decode(raw);
}

/** Convert a ModbusReadEntryT back to an EditorReadEntry. */
function fbReadToEditor(entry: ModbusReadEntryT): EditorReadEntry {
  // ValuedMessageT is the source of truth; label is legacy fallback only
  const fromValuedMsg = entry.ln2Message ? valuedMessageTToCompositeKey(entry.ln2Message) : null;
  const msgId = fromValuedMsg || decodeLabel(entry.label) || "";
  const pairedFromValuedMsg = entry.pairedLn2Message
    ? valuedMessageTToCompositeKey(entry.pairedLn2Message)
    : null;
  return {
    registerAddress: entry.registerAddress,
    readType: entry.readType,
    quantity: entry.quantity,
    interpretation: entry.interpretation,
    bit: entry.bit,
    bitsPerRegister: entry.bitsPerRegister,
    expectedValue: entry.expectedValue,
    msgId,
    active: entry.ln2Message?.active ?? true,
    floorsBased: entry.floorsBased,
    unitsBased: entry.unitsBased,
    bankLevel: false,
    label: null,
    pairedMsgId: pairedFromValuedMsg,
    pairedActive: entry.pairedLn2Message?.active ?? false,
  };
}

/** Convert a ModbusWriteEntryT back to an EditorWriteEntry. */
function fbWriteToEditor(entry: ModbusWriteEntryT): EditorWriteEntry {
  const fromValuedMsg = entry.ln2Message ? valuedMessageTToCompositeKey(entry.ln2Message) : null;
  const msgId = fromValuedMsg || decodeLabel(entry.label) || "";
  return {
    registerAddress: entry.registerAddress,
    writeType: entry.writeType,
    interpretation: entry.interpretation,
    bit: entry.bit,
    bitsPerRegister: entry.bitsPerRegister,
    andMask: entry.andMask,
    orMask: entry.orMask,
    writeValue: entry.writeValue,
    msgId,
    active: entry.ln2Message?.active ?? true,
    floorsBased: entry.floorsBased,
    unitsBased: entry.unitsBased,
    bankLevel: false,
    label: null,
  };
}

/**
 * Unpack FlatBuffer binary bytes into the UI editor state.
 */
export function unpackModbusTemplate(bytes: Uint8Array): EditorModbusTemplate {
  const buf = new flatbuffers.ByteBuffer(bytes);
  const fb = ModbusTemplate.getRootAsModbusTemplate(buf);
  const t = fb.unpack();

  return {
    reads: (t.reads ?? []).map(fbReadToEditor),
    writes: (t.writes ?? []).map(fbWriteToEditor),
    registerOffsets: modbusRegisterOffsetsFbToUi(t.registerOffsets),
    slaveId: t.slaveId,
    pollingInterval: t.pollingInterval,
  };
}
